/**
 * `15499` — *create an {item, itemMapping} in {board, targetBoardId} and connect
 * {boards, linkageBoards} with {column, linkageColumn}*.
 *
 * The most-used unimplemented block on this account: **16 production nodes** across 9 custom
 * recipes plus the replacement chains of static recipes `222`, `225` and `226`.
 *
 * Which side holds the connect column is not guessed — it is in monday's own
 * `configurationFields`: recipe `225` declares `linkToPulseColumnId.linkedToBoard =
 * "sourceBoardId"` and `boardIdField = "targetBoardId"` (the column lives on the **target**
 * board and points back here), while recipe `223` declares `linkedToBoard = "targetBoardId"`
 * (the column lives on **this** board). The block therefore takes the column's own board
 * explicitly and defaults to the source board, which is what the `linkageBoards` slot of the
 * new-builder form carries.
 */
import { z } from 'zod';
import { defineAction } from '../block.js';
import { applyMappedCells, itemMappingSchema, mappingSourceColumns, resolveItemMapping } from '../item-mapping.js';
import { columnRefSchema, numberish } from '../values.js';
/**
 * `linkageBoards` is a board id, a list of them, `{value}`-shaped, or — the shape monday's own
 * builder stores on every imported custom recipe of this block (38 active ones on the account,
 * P-290) — `{boardsIds: [...], invalid}`. All four occur; the fourth was the one the schema
 * rejected, so every "create an item in board and connect boards" chain on MK-PLAN-Ads failed
 * with `invalid_params` at the action after its trigger and conditions had matched.
 */
const linkageBoardsSchema = z
    .union([
    numberish,
    z.array(numberish),
    z.looseObject({ boardsIds: z.array(numberish) }),
    z.looseObject({ boardIds: z.array(numberish) }),
    z.looseObject({ value: numberish }),
    z.null(),
])
    .optional()
    .transform((raw) => {
    if (raw === null || raw === undefined)
        return [];
    if (Array.isArray(raw))
        return raw;
    if (typeof raw === 'object') {
        if ('boardsIds' in raw && Array.isArray(raw.boardsIds))
            return raw.boardsIds;
        if ('boardIds' in raw && Array.isArray(raw.boardIds))
            return raw.boardIds;
        if ('value' in raw)
            return [raw.value];
        return [];
    }
    return [raw];
});
export const createItemAndConnectParams = z.object({
    targetBoardId: numberish.nullable().default(null),
    itemMapping: itemMappingSchema,
    linkageBoards: linkageBoardsSchema,
    /** The `board-relation` column that gets the link. */
    linkageColumn: columnRefSchema.nullable().default(null),
    sourceBoardId: numberish.nullable().optional(),
});
/** Merges one id into a `board_relation` value without dropping the links already there. */
export function withLinkedItem(current, itemId) {
    const existing = typeof current === 'object' && current !== null && Array.isArray(current.linkedPulseIds)
        ? (current.linkedPulseIds ?? [])
        : [];
    const ids = new Set();
    for (const entry of existing) {
        const value = Number(entry.linkedPulseId);
        if (Number.isFinite(value))
            ids.add(value);
    }
    ids.add(Number(itemId));
    return { linkedPulseIds: [...ids].map((linkedPulseId) => ({ linkedPulseId })) };
}
export const createItemAndConnectAction = defineAction({
    id: '15499',
    kind: 'action',
    app: 'monday',
    sentencePartial: 'create an {item, itemMapping} in {board, targetBoardId} and connect {boards, linkageBoards} with {column, linkageColumn}',
    paramsSchema: createItemAndConnectParams,
    executable: true,
    references: (params) => [
        ...(params.targetBoardId === null ? [] : [{ kind: 'board', id: String(params.targetBoardId) }]),
        ...(params.linkageColumn === null
            ? []
            : [{ kind: 'column', id: params.linkageColumn.columnId, invalid: params.linkageColumn.invalid }]),
        ...mappingSourceColumns(params.itemMapping).map((columnId) => ({ kind: 'column', id: columnId })),
    ],
    async execute(context, params) {
        if (params.targetBoardId === null)
            return { skipped: 'no_target_board' };
        const targetBoardId = String(params.targetBoardId);
        const resolved = await resolveItemMapping(context, params.itemMapping);
        const created = await context.services.createItem({
            boardId: targetBoardId,
            groupId: resolved.groupId,
            name: resolved.name === null || resolved.name.length === 0 ? 'New item' : resolved.name,
        });
        const applied = await applyMappedCells(context, { boardId: targetBoardId, itemId: created.id }, resolved.cells);
        let linked = null;
        if (params.linkageColumn !== null && context.itemId !== null) {
            // The column may live on either side; `columnRef.boardId` is monday's own answer when the
            // config carries one, otherwise the link is written on the board the run is on.
            const columnBoardId = params.linkageColumn.boardId === undefined ? context.boardId : String(params.linkageColumn.boardId);
            const holderItemId = columnBoardId === targetBoardId ? created.id : context.itemId;
            const otherItemId = columnBoardId === targetBoardId ? context.itemId : created.id;
            const column = await context.services.getColumn(columnBoardId, params.linkageColumn.columnId);
            if (column !== null) {
                const current = await context.services.getCellValue(holderItemId, params.linkageColumn.columnId);
                await context.services.setCellValue({
                    boardId: columnBoardId,
                    itemId: holderItemId,
                    columnId: params.linkageColumn.columnId,
                    value: withLinkedItem(current, otherItemId),
                });
                linked = { boardId: columnBoardId, itemId: holderItemId, columnId: params.linkageColumn.columnId };
            }
        }
        return {
            itemId: created.id,
            boardId: targetBoardId,
            groupId: created.groupId,
            name: created.name,
            written: applied.written,
            failed: applied.failed,
            linked,
            linkageBoards: params.linkageBoards.map(String),
        };
    },
});
//# sourceMappingURL=15499.js.map